import { useState } from 'react';
import {
  AiTextGeneratorFormValues,
  XAiInputs,
} from '../AiConfigs/constants';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PrettoSlider } from '../../assets/styles/styles';
import { useAuthStore } from '../../store/storeAuth';

type Props = {
  watch: AiTextGeneratorFormValues;
  onChange: (e: any) => void;
  tokenCount?: number;
  systems?: any[];
};

const modelInputBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '4px',
};

const XAi = ({ onChange, watch, tokenCount, systems }: Props) => {
  const [isAutoSetting, setIsAutoSetting] = useState(watch.autoToken);
  const { user } = useAuthStore(state => state);

  const XAiSystem = systems?.filter((item: any) => item.system === 'xai')[0];
  const textModels = (XAiSystem?.models || []).filter(
    (m: any) => m.type === 'Text' && !m.isDeprecated,
  );

  return (
    <Box>
      <Box mt={'4px'}>
        <Typography
          fontSize={'14px'}
          fontWeight={500}
          sx={{
            mb: '4px',
            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
          }}
          variant="body1"
        >
          Model
        </Typography>
        <FormControl sx={{ width: '100%' }}>
          <Select
            className="nodrag"
            name="aiModel"
            fullWidth
            size="small"
            defaultValue={'grok-4-0709'}
            value={watch.aiModel}
            sx={{
              height: 'unset',
              bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            }}
            onChange={onChange}
            MenuProps={{
              style: {
                maxHeight: '700px',
              },
            }}
          >
            {textModels.map((opt: any, index: number) => (
              <MenuItem
                className="nodrag"
                key={opt.model + index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignItems: 'start',
                  padding: '16px',
                }}
                value={opt.model}
                disabled={opt.enabled === false || opt.disabledByCompany}
              >
                <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                  <Typography
                    mb={'4px'}
                    textTransform={'capitalize'}
                    variant="body2"
                    color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                  >
                    {opt.displayName || opt.model}
                  </Typography>
                  {opt.disabledByCompany && (
                    <Typography fontSize={'12px'} color="red">
                      (disabled by admin)
                    </Typography>
                  )}
                </Box>
                <Box
                  display={'flex'}
                  gap={'8px'}
                  alignItems={'center'}
                  flexWrap={'wrap'}
                >
                  {opt?.tags?.map((tag: any, index: number) => {
                    const tagLabel = typeof tag === 'string' ? tag : tag.label;
                    const tagTooltip =
                      typeof tag === 'string' ? '' : tag.tooltip;

                    return (
                      <Tooltip
                        key={tagLabel + index}
                        title={tagTooltip}
                        arrow
                        placement="top"
                      >
                        <Chip
                          sx={{
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 600,
                            border: `1px solid ${
                              user?.theme === 'dark' ? '#475467' : '#EAECF0'
                            }`,
                            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                          }}
                          label={tagLabel}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Typography
                  maxWidth={'570px'}
                  variant="body2"
                  sx={{
                    color: '#667085',
                    mt: '8px',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'start',
                  }}
                >
                  {opt.description}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          mt: '20px',
        }}
      >
        {XAiInputs.map(opt => {
          const optName = opt.name;
          if (opt.name === 'aiTokens') {
            return (
              <Box
                sx={{
                  ...modelInputBoxStyle,
                  alignItems: 'center',
                  mt: '20px',
                }}
                key={optName}
              >
                <FormControlLabel
                  control={
                    <Switch
                      onChange={e => {
                        onChange({
                          target: {
                            name: 'autoToken',
                            value: !watch.autoToken,
                          },
                        });

                        setIsAutoSetting(!watch.autoToken);
                      }}
                      name="autoToken"
                      checked={watch.autoToken}
                      sx={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    />
                  }
                  label="Auto setting"
                />
                <FormControl>
                  <TextField
                    id="demo-simple-select"
                    type={opt.type}
                    // style={{ marginLeft: '8px', width: '100px' }}
                    onChange={onChange}
                    value={isAutoSetting ? tokenCount : watch.aiTokens}
                    disabled={isAutoSetting}
                    className="nodrag"
                    label={'Output Token Max'}
                    name={opt.name}
                    InputProps={{
                      style: {
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      },
                    }}
                    sx={{
                      width: '290px',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      border: `2px solid ${
                        user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                    }}
                  />
                </FormControl>
              </Box>
            );
          }
          return (
            <Box key={opt.name}>
              <Typography
                variant="body1"
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                }}
              >
                {opt.label}
              </Typography>
              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                <FormLabel htmlFor={opt.name}>
                  {watch?.[optName as keyof AiTextGeneratorFormValues]}
                </FormLabel>

                <PrettoSlider
                  step={Number(opt.step)}
                  min={Number(opt.min)}
                  max={Number(opt.max)}
                  name={opt.name}
                  value={Number(
                    watch?.[optName as keyof AiTextGeneratorFormValues],
                  )}
                  onChange={onChange}
                  className="nodrag"
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default XAi;
